import { TamediaStyles } from '@ta-interaktiv/microfrontend-template-helpers'
import { FC, ReactElement } from 'react'
import styled, { css } from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'
import breakpointVariables from '../../Globals/breakpoints.module.css'

import './map.module.css'

import { MFContextProvider, PartSplitter } from 'components/MFHelpers'
import GDEMap from 'components/GDEMap'
import MainPart from 'components/MainPart/MainPart'
import CantonsMap from 'components/CantonsMap/CantonsMap'

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties,
}): ReactElement => {
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    breakpointVariables.globalBreakpoints,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]
console.log("TENANT", tenant)
  return (
    <AppContainer className={globalClasses.join(' ')} $colorMode={colorMode}>
      <MFContextProvider
        locale={tenant.lang === 'de' ? 'de' : 'fr'}
        colorMode={colorMode}
        translations={{
          de: {
            inGwh: 'in GWh',  
            inPercent: 'in %',
            anlagen: 'Anlagen',

            muni: 'Gemeinde',
            switzerland: 'Schweiz',
            canton: 'Kanton',
            cantonAbbr: 'Kt.',
            showless: 'Zeige Weniger',
            showall: 'Zeige Alle',
            sat_disclaimer:
              'Die Satellitenkarte zeigt nicht den aktuellsten Stand. Neuere Solaranlagen sind daher zwar als Punkte eingezeichnet, auf dem hinterlegten Satellitenbild aber möglicherweise nicht zu sehen. In der Karte können zudem einzelne Anlagen wegen fehlender Koordinaten nicht eingezeichnet sein.',
            search_your_municipality: 'Suchen Sie ihre Gemeinde',
            linechart_title: 'Ausbau der Solarenergie in {{name}} seit 2010',
            linechart_subtitle: 'Genutztes Potenzial der Dachflächen',
            linechart_hint:
              'Für die Berechnung des genutzten Potenzials werden die Dachflächen von 2023 verwendet, was die Angaben von früheren Jahren leicht verzerren kann.',
            linechart_source: 'Eigene Berechnung, BFE Daten herunterladen',
            source: 'Quelle',
            gdemap_title:
              'Ausbau der Solarenergie in den Gemeinden bis Ende 2024',
            gdemap_subtitle: 'Ausnutzung des Potenzials in %',
            gdemap_search_placeholder: 'Gemeinde suchen',
            gde_potential: 'Potenzial',
            gde_usage: 'Ausnutzung',
            gde_production: 'Produktion',
            cantonstable_title: 'Appenzell top, Graubünden flop',
            cantonstable_subtitle:
              'Ausbau der Solarenergie in den Kantonen bis Ende 2024',
            no_solar_data: 'In dieser Gemeinde gibt es keine Solaranlagen.',
            solars: 'Solaranlagen',
            used_potential: 'des Solarpotenzials ausgenutzt',
            per_year: 'pro Jahr',
            energy_for: 'Strom für',
            households: 'Haushalte',
            solar_count: 'Anzahl Solaranlagen',
            total_production: 'Gesamtproduktion',
            production: 'Produktion',
            total_potential: 'Gesamtpotenzial',
            effective_potential: 'Genutztes Potenzial',
          },
          fr: {
            inGwh: 'en GWh',
            inPercent: 'en %',
            anlagen: 'Installations',
            muni: 'Commune',
            switzerland: 'Suisse',
            canton: 'Canton',
            cantonAbbr: 'Ct.',
            showless: 'Afficher moins',
            showall: 'Afficher tout',
            sat_disclaimer:
              'La carte satellite ne reflète pas nécessairement l’état le plus récent. Certaines installations récentes sont bien indiquées sous forme de points, mais peuvent ne pas apparaître sur l’image satellite en arrière-plan. Certaines installations peuvent ne pas être visibles sur la carte en raison de coordonnées manquantes.',
            search_your_municipality: 'Recherchez votre commune',
            linechart_title:
              "Développement de l'énergie solaire à {{name}} depuis 2010",
            linechart_subtitle: 'Potentiel utilisé des surfaces de toitures',
            linechart_hint:
              'Pour le calcul du potentiel utilisé, les surfaces de toiture de 2023 sont utilisées, ce qui peut légèrement fausser les données des années précédentes.',
            linechart_source: 'analyse interne, télécharger les données OFEN',
            source: 'Source',
            gdemap_title:
              "Développement de l'énergie solaire dans les communes jusqu'à fin 2022",
            gdemap_subtitle: 'Utilisation du potentiel en %',
            gdemap_search_placeholder: 'Rechercher une commune',
            gde_potential: 'Potentiel',
            gde_usage: 'Utilisation',
            gde_production: 'Production',
            cantonstable_title: 'Appenzell en tête, les Grisons à la peine',
            cantonstable_subtitle:
              "Développement de l'énergie solaire dans les cantons jusqu'à fin 2024",
            no_solar_data:
              "Il n'y a pas de données solaires pour cette commune.",
            solars: 'Installations solaires',
            used_potential: 'du potentiel est exploité',
            per_year: 'par an',
            energy_for: "De l'électricité pour",
            households: 'ménages',
            solar_count: "Nombre d'installations solaires",
            total_production: 'Production totale',
            production: 'Production',
            total_potential: 'Potentiel total',
            effective_potential: 'Potentiel utilisé',
          },
        }}
        tenantKey={tenant.key}
        properties={properties}
        trackEvent={() => {}}
      >
        <PartSplitter
          currentPart={properties.part}
          parts={{
            3: <CantonsMap />,
            1: <GDEMap />,
            2: <MainPart />,
          }}
        />
      </MFContextProvider>
    </AppContainer>
  )
}

/*

  colorMode === 'light'
      ? ['var(--gray-4)', 'var(--gray-2)', '#ffcf31']
      : ['var(--gray-3)', 'var(--gray-5)', '#ffcf31']

      */

const AppContainer = styled(TamediaStyles)`
  // 🌞 all custom Light Mode variables
  background-color: var(--site-background);
  --button-bg-color: var(--beige-bright-2);
  --button-label-color: var(--gray-3);
  --info-bg-color: var(--beige-bright-2);
  --tablerow-bg: var(--gray-6);
  --data-table-bar-bg: var(--gray-5);
  --data-table-border: var(--gray-5);
  --data-table-selected-bg: var(--gray-4);
  --data-table-selected-text: var(--gray-1);
  --data-table-selected-bar-bg: var(--gray-5);
  --data-table-selected-bar-text: var(--gray-2);
  ${({ $colorMode: colorMode }) =>
    colorMode === 'dark'
      ? css`
          --tablerow-bg: var(--gray-2);
          --data-table-bar-bg: var(--gray-3);
          --data-table-border: var(--gray-2);
          --data-table-selected-bg: var(--gray-4);
          --data-table-selected-text: var(--gray-1);
          --data-table-selected-bar-bg: var(--gray-3);
          --data-table-selected-bar-text: var(--gray-6);
          // 🌚 all custom Dark Mode variables
        `
      : ''}
`
// const AppContainer = styled(TamediaStyles)<{ colorMode?: string }>`
//   position: relative;
//   width: 100%;
//   margin: 0;
//   padding: 0;
//   font-family: var(--font-sans-serif);
//   color: var(--brandblue-main);
//   font-size: 18px;
//   -webkit-font-smoothing: antialiased;
//   -moz-osx-font-smoothing: grayscale;
//   margin-bottom: 60px;
//   overflow: hidden;
//   .municipality-search {
//     position: relative;
//     z-index: 1002;
//   }
//   code {
//     font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
//       monospace;
//   }
// `
export default MainComponent

function isValidGeoJSON(data: any) {
  try {
    // Check basic structure
    if (!data || typeof data !== 'object') return false

    // Check for required type property
    if (!data.type) return false

    // For FeatureCollection
    if (data.type === 'FeatureCollection') {
      return Array.isArray(data.features)
    }

    // For Feature
    if (data.type === 'Feature') {
      return data.geometry && data.geometry.type && data.geometry.coordinates
    }

    // For direct geometry
    return data.coordinates && Array.isArray(data.coordinates)
  } catch (e) {
    console.error('GeoJSON validation error:', e)
    return false
  }
}
